<template>
    <page-title
        title="先生アカウント編集"
        icon="bi-person-fill"
        :backTo="{ name: 'PageSettingTeacherList' }"
    >
    </page-title>

    <inline-loader v-if="loading"></inline-loader>

    <div v-else>
        <summary-box title="学校">
            <summary-box-row title="学校名" :text="school.school_name"></summary-box-row>
            <summary-box-row title="所在地" :text="school.full_address"></summary-box-row>
            <summary-box-row title="学校コード" :text="school.school_code"></summary-box-row>
        </summary-box>

        <hr>

        <inline-loader v-if="loading_body"></inline-loader>

        <form v-else @submit.prevent="fixSave">
            <div class="row mt-1">
                <div class="col-12 col-md-6">
                    <label class="form-label">先生名</label>
                    <form-input
                        v-model="teacher.teacher_name"
                        :required="true"
                    >
                    </form-input>
                </div>
            </div>
            <div class="row mt-1">
                <div class="col-20 col-md-10">
                    <label class="form-label">メールアドレス</label>
                    <form-input
                        v-model="teacher.email"
                    >
                    </form-input>
                </div>
            </div>
            <div class="row mt-1">
                <div class="col-10 col-md-5">
                    <label class="form-label">権限</label>
                    <form-select
                        v-model="teacher.role"
                        :options="school_roles"
                        empty_option="-- 選択 --"
                        :required="true"
                    ></form-select>
                </div>
            </div>
            <div class="row mt-1">
                <div class="col-12 col-md-6">
                    <label class="form-label">ログインID</label>
                    <form-input
                        v-model="teacher.user_login"
                        :required="true"
                    >
                    </form-input>
                </div>
            </div>
            <div class="row mt-1 mb-4">
                <div class="col-24">
                    <label class="form-label">
                        パスワード
                    </label>
                    <div class="col-12 col-md-6 mb-1">
                        <form-input
                            v-model="teacher.password"
                        >
                        </form-input>
                    </div>
                    <text-info class="mb-0">
                        ＊変更する場合のみ入力してください<br>
                        ＊パスワードは英数字６文字以上で入力してください。（英字・数字どちらも含む）
                    </text-info>
                </div>
            </div>
            <button-wrap>
                <template v-slot:default>
                    <button-exec-edit text="更新" />
                </template>
                <template v-slot:negative>
                    <button-exec-remove type="button" text="強制的にログアウト" icon="bi-box-arrow-right" @click="$refs.confirm_kickout.show()" />
                    <button-exec-remove type="button" text="削除" @click="$refs.confirm_remove.show()" />
                </template>
            </button-wrap>
        </form>
    </div>

    <confirm-dialog ref="confirm_kickout" @ok="kickout" text_cancel="中止">
        <p>この先生を強制的にログアウトさせてもよろしいですか？</p>
    </confirm-dialog>
    <confirm-dialog ref="confirm_remove" @ok="remove" text_cancel="中止">
        <p>この先生を削除してもよろしいですか？</p>
    </confirm-dialog>
</template>

<script>
import School from '@/shared/models/entities/school'
import Teacher from '@/shared/models/entities/teacher'
import SchoolRole from '@/shared/models/enums/school-role'
import ButtonWrap from '@/shared/components/design/buttons/ButtonWrap';
import ButtonExecEdit from '@/shared/components/design/buttons/ButtonExecEdit';
import ButtonExecRemove from '@/shared/components/design/buttons/ButtonExecRemove'
import PageTitle from '@/shared/components/design/PageTitle'
import SummaryBox from '@/shared/components/design/SummaryBox'
import SummaryBoxRow from '@/shared/components/design/SummaryBoxRow'
import TextInfo from '@/shared/components/design/TextInfo'
import FormInput from '@/shared/components/form/FormInput'
import FormSelect from '@/shared/components/form/FormSelect';
import ConfirmDialog from '@/shared/components/tool/ConfirmDialog';
import InlineLoader from '@/shared/components/tool/InlineLoader'

export default {
    name: 'PageSettingTeacherEdit',
    components: {
        ButtonWrap,
        ButtonExecEdit,
        ButtonExecRemove,
        PageTitle,
        FormInput,
        FormSelect,
        SummaryBox,
        SummaryBoxRow,
        TextInfo,
        ConfirmDialog,
        InlineLoader,
    },
    inject: [
        'startScreenLoading',
        'endScreenLoading',
        'showMessage',
        'showErrorMessage'
    ],
    data() {
        return {
            school: new School(),
            teacher: new Teacher(),

            SchoolRole,
            school_roles: SchoolRole.options(),

            loading: true,
            loading_body: true,

        }
    },
    mounted() {
        // 学校取得
        this.$http.get('/school/me')
        .then(response => {
            this.school = new School(response.data);
            this.loading = false;

            // 先生を取得
            this.$http.get(`/school/teacher/${parseInt(this.$route.params.id, 10)}`)
            .then(response => {
                this.teacher = new Teacher(response.data);
                //権限設定がnullの場合に初期値を設定
                if (this.$helper.isNud(this.teacher.role)) {
                    this.teacher.role = SchoolRole.NORMAL;
                }
                this.loading_body = false;
            });
        })
        .finally(() => {
        });
    },
    methods: {
        //保存
        fixSave() {
            this.startScreenLoading();
            this.$http.put(`/school/teacher/${parseInt(this.$route.params.id, 10)}`, this.teacher)
            .then(() => {
                this.showMessage('先生情報を更新しました');
            })
            .finally(() => {
                this.endScreenLoading();
            });
        },

        //強制ログアウト
        kickout() {
            this.startScreenLoading();
            this.$http.post(`/school/teacher/${parseInt(this.$route.params.id, 10)}/kick`)
            .then(() => {
                this.showMessage('「' + this.teacher.teacher_name + '」先生をログアウトさせました');
            })
            .finally(() => {
                this.endScreenLoading();
            });
        },

        //削除
        remove() {
            this.startScreenLoading();
            this.$http.delete(`/school/teacher/${parseInt(this.$route.params.id, 10)}`)
            .then(() => {
                this.showMessage('「' + this.teacher.teacher_name + '」先生を削除しました');
                //リダイレクト
                this.$router.push({name: 'PageSettingTeacherList'});
            })
            .finally(() => {
                this.endScreenLoading();
            });
        },
    },
    computed: {
    }
}
</script>

<style scoped>

</style>
